<template>
  <div class="home">
    <!-- 轮播图区域 -->
    <section class="hero-slider">
      <div class="swiper-container">
        <div class="swiper-wrapper">
          <div class="swiper-slide" v-for="(slide, index) in slides" :key="index">
            <div class="slide-bg" :style="{ backgroundImage: `url(${slide.image})` }">
              <div class="slide-overlay">
                <div class="container">
                  <div class="slide-content">
                    <span class="slide-eyebrow">{{ $t('home.eyebrow') }}</span>
                    <h1 class="slide-title">{{ slide.title }}</h1>
                    <p class="slide-description">{{ slide.description }}</p>
                    <div class="slide-buttons">
                      <button class="btn btn-primary btn-lg" @click="goToAbout">{{ $t('home.ctaContact') }}</button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="swiper-pagination"></div>
        <div class="swiper-button-prev"></div>
        <div class="swiper-button-next"></div>
      </div>
    </section>

    <!-- 企业定位 / 愿景 -->
    <section class="vision-section text-white py-5">
      <div class="container">
        <div class="text-center mb-5">
          <h2 class="vision-headline">{{ $t('home.visionHeadline') }}</h2>
          <p class="vision-sub">{{ $t('home.visionSub') }}</p>
        </div>
        <div class="row">
          <div class="col-lg-4 mb-4 wow animate__fadeInUp" :data-wow-delay="(index * 0.15) + 's'" v-for="(pillar, index) in pillars" :key="pillar.id">
            <div class="pillar-card h-100">
              <div class="pillar-icon"><i :class="pillar.icon"></i></div>
              <h4 class="pillar-title">{{ pillar.title }}</h4>
              <p class="pillar-text mb-0">{{ pillar.text }}</p>
            </div>
          </div>
        </div>
        <div class="vision-stat text-center mt-3" v-html="$t('home.visionStat')"></div>
      </div>
    </section>

    <!-- 公司介绍 / SEO品牌与业务信息 -->
    <section class="company-profile-section py-5">
      <div class="profile-glow profile-glow-one"></div>
      <div class="profile-glow profile-glow-two"></div>
      <div class="container position-relative">
        <div class="company-profile-panel wow animate__fadeInUp">
          <div class="profile-heading text-center">
            <span class="profile-kicker">ABOUT VICTORY AI</span>
            <h2 class="section-title">{{ $t('home.companyIntroTitle') }}</h2>
            <p class="section-subtitle mb-0">{{ $t('home.companyIntroSub') }}</p>
          </div>
          <div class="profile-copy">
            <p v-for="(paragraph, index) in $t('home.companyIntroParagraphs')" :key="index">
              {{ paragraph }}
            </p>
          </div>
          <div class="profile-tags" aria-label="核心业务关键词">
            <span v-for="tag in $t('home.companyIntroTags')" :key="tag">{{ tag }}</span>
          </div>
        </div>
      </div>
    </section>

    <!-- 核心产品矩阵 / AI使能技术 -->
    <section class="matrix-section py-5">
      <div class="container">
        <div class="section-header text-center mb-5">
          <h2 class="section-title">{{ $t('home.matrixTitle') }}</h2>
          <p class="section-subtitle">{{ $t('home.matrixSub') }}</p>
        </div>
        <div class="row justify-content-center">
          <div class="col-lg-4 col-md-4 col-6 mb-4 wow animate__zoomIn" :data-wow-delay="(index % 3 * 0.1) + 's'" v-for="(m, index) in matrix" :key="m.name">
            <div class="matrix-card">
              <div class="matrix-logo">
                <img :src="m.logo" :alt="m.name + ' logo'">
              </div>
              <h5 class="matrix-name">{{ m.name }}</h5>
              <p class="matrix-desc">{{ m.desc }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 行业覆盖 -->
    <section class="industries-section bg-light py-5">
      <div class="container">
        <div class="section-header text-center mb-5">
          <h2 class="section-title">{{ $t('home.industriesTitle') }}</h2>
          <p class="section-subtitle">{{ $t('home.industriesSub') }}</p>
        </div>
        <div class="row justify-content-center">
          <div class="col-lg-2 col-md-4 col-6 mb-4 wow animate__fadeInUp" :data-wow-delay="(index * 0.08) + 's'" v-for="(industry, index) in industries" :key="industry.id">
            <div class="industry-item">
              <div class="industry-img">
                <img :src="industry.image" :alt="industry.name">
              </div>
              <h6 class="mb-0">{{ industry.name }}</h6>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 研发实力 -->
    <section class="strength-section py-5 text-white">
      <div class="container">
        <div class="section-header text-center mb-5">
          <h2 class="section-title text-white">{{ $t('home.strengthTitle') }}</h2>
          <p class="section-subtitle text-white-50">{{ $t('home.strengthSub') }}</p>
        </div>
        <div class="row text-center">
          <div class="col-lg-2 col-md-4 col-6 mb-4 wow animate__fadeInUp" :data-wow-delay="(index * 0.08) + 's'" v-for="(stat, index) in stats" :key="stat.id">
            <div class="stat-box">
              <div class="stat-num">{{ stat.display }}<span class="stat-suffix">{{ stat.suffix }}</span></div>
              <div class="stat-label">{{ statLabels[index] }}</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import Swiper from 'swiper'
import { WOW } from 'wowjs'
import { heroArt } from '../data/productArt'
const heroAI = heroArt('ai')
const heroEdge = heroArt('edge')
const heroCarbon = heroArt('carbon')
import indEnergy from '../assets/industry/ind_energy.png'
import indCoal from '../assets/industry/ind_coal.png'
import indChem from '../assets/industry/ind_chem.png'
import indMech from '../assets/industry/ind_mech.png'
import indAgri from '../assets/industry/ind_agri.png'
import indOther from '../assets/industry/ind_other.png'

function matrixLogo(label, accent = '#2f80ed', motif = 'model') {
  const motifs = {
    model: '<circle cx="90" cy="90" r="34"/><path d="M90 34v22M90 124v22M34 90h22M124 90h22M50 50l16 16M130 50l-16 16M50 130l16-16M130 130l-16-16"/>',
    smallModel: '<rect x="52" y="54" width="76" height="76" rx="18"/><path d="M70 34v20M90 34v20M110 34v20M70 130v20M90 130v20M110 130v20M32 72h20M32 90h20M32 108h20M128 72h20M128 90h20M128 108h20"/>',
    vision: '<path d="M32 90c34-42 82-42 116 0-34 42-82 42-116 0z"/><circle cx="90" cy="90" r="26"/><circle cx="90" cy="90" r="9"/>',
    voice: '<path d="M72 54v72M108 54v72M54 76v28M126 76v28M36 86v8M144 86v8"/><path d="M58 138c20 14 44 14 64 0"/>',
    software: '<rect x="40" y="48" width="100" height="84" rx="14"/><path d="M40 74h100M66 102l-18 18 18 18M114 102l18 18-18 18M84 138l18-44"/>',
    twin: '<path d="M90 38l46 26v52l-46 26-46-26V64z"/><path d="M90 38v52l46 26M90 90l-46 26M44 64l46 26 46-26"/>',
    robot: '<rect x="50" y="56" width="80" height="72" rx="20"/><circle cx="74" cy="92" r="8"/><circle cx="106" cy="92" r="8"/><path d="M90 56V34M60 128l-22 24M120 128l22 24"/>',
    sensor: '<circle cx="90" cy="90" r="24"/><path d="M90 44v-18M90 154v-18M44 90H26M154 90h-18M58 58L45 45M122 58l13-13M58 122l-13 13M122 122l13 13"/><path d="M70 90h40"/>',
    compute: '<rect x="48" y="58" width="84" height="64" rx="12"/><path d="M66 78h48M66 100h48M68 38v20M90 38v20M112 38v20M68 122v20M90 122v20M112 122v20"/>'
  }
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="180" height="180" viewBox="0 0 180 180">
      <defs>
        <linearGradient id="g" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stop-color="${accent}"/>
          <stop offset="100%" stop-color="#071b35"/>
        </linearGradient>
      </defs>
      <rect width="180" height="180" rx="42" fill="url(#g)"/>
      <circle cx="136" cy="38" r="34" fill="#fff" opacity="0.14"/>
      <circle cx="42" cy="142" r="46" fill="#66d9ff" opacity="0.12"/>
      <g fill="none" stroke="#fff" stroke-width="8" stroke-linecap="round" stroke-linejoin="round" opacity="0.9">
        ${motifs[motif] || motifs.model}
      </g>
      <text x="90" y="102" text-anchor="middle" fill="#fff" font-size="22" font-weight="700" font-family="Arial, 'Microsoft YaHei', sans-serif">${label}</text>
    </svg>
  `
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`
}

const heroImages = [heroAI, heroEdge, heroCarbon]
const pillarIcons = ['fas fa-eye', 'fas fa-compass', 'fas fa-microchip']
const industryImages = [indEnergy, indCoal, indChem, indMech, indAgri, indOther]
const matrixMeta = [
  { accent: '#2f80ed', motif: 'model' },
  { accent: '#00a6a6', motif: 'smallModel' },
  { accent: '#9b51e0', motif: 'vision' },
  { accent: '#eb5757', motif: 'voice' },
  { accent: '#2d9cdb', motif: 'software' },
  { accent: '#6c5ce7', motif: 'twin' },
  { accent: '#f2994a', motif: 'robot' },
  { accent: '#27ae60', motif: 'sensor' },
  { accent: '#56ccf2', motif: 'compute' }
]

export default {
  name: 'Home',
  data() {
    return {
      stats: [
        { id: 1, num: 28, suffix: '', display: 0 },
        { id: 2, num: 12, suffix: '', display: 0 },
        { id: 3, num: 100, suffix: '+', display: 0 },
        { id: 4, num: 70, suffix: '%', display: 0 },
        { id: 5, num: 10, suffix: '+', display: 0 },
        { id: 6, num: 60, suffix: '+', display: 0 }
      ]
    }
  },
  computed: {
    slides() {
      return this.$t('home.slides').map((s, i) => ({ title: s.title, description: s.description, image: heroImages[i] }))
    },
    pillars() {
      return this.$t('home.pillars').map((p, i) => ({ id: i + 1, icon: pillarIcons[i], title: p.title, text: p.text }))
    },
    matrix() {
      return this.$t('home.matrix').map((m, i) => ({
        name: m.name,
        desc: m.desc,
        logo: matrixLogo(m.label, matrixMeta[i].accent, matrixMeta[i].motif)
      }))
    },
    industries() {
      return this.$t('home.industries').map((name, i) => ({ id: i + 1, name, image: industryImages[i] }))
    },
    statLabels() {
      return this.$t('home.statLabels')
    }
  },
  mounted() {
    this.initSwiper()
    this.initWOW()
    this.initCounters()
  },
  beforeDestroy() {
    if (this._io) this._io.disconnect()
  },
  methods: {
    initSwiper() {
      this.$nextTick(() => {
        new Swiper('.swiper-container', {
          loop: true,
          autoplay: { delay: 5000, disableOnInteraction: false },
          pagination: { el: '.swiper-pagination', clickable: true },
          navigation: { nextEl: '.swiper-button-next', prevEl: '.swiper-button-prev' },
          effect: 'fade',
          fadeEffect: { crossFade: true }
        })
      })
    },
    initWOW() {
      try {
        new WOW({ boxClass: 'wow', animateClass: 'animate__animated', offset: 60, mobile: true, live: false }).init()
      } catch (e) { /* noop */ }
    },
    initCounters() {
      const section = this.$el.querySelector('.strength-section')
      if (!section || typeof IntersectionObserver === 'undefined') {
        this.stats.forEach(s => { s.display = s.num })
        return
      }
      this._io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.runCount()
            this._io.disconnect()
          }
        })
      }, { threshold: 0.3 })
      this._io.observe(section)
    },
    runCount() {
      const duration = 1400
      const start = performance.now()
      const step = (now) => {
        const p = Math.min((now - start) / duration, 1)
        const ease = 1 - Math.pow(1 - p, 3)
        this.stats.forEach((s) => { s.display = Math.round(s.num * ease) })
        if (p < 1) requestAnimationFrame(step)
      }
      requestAnimationFrame(step)
    },
    goToAbout() {
      this.$router.push('/about')
    }
  }
}
</script>

<style scoped>
/* 轮播图 */
.hero-slider { position: relative; height: 440px; overflow: hidden; }
.swiper-container { width: 100%; height: 100%; }
.slide-bg {
  width: 100%; height: 440px;
  background-size: cover; background-position: center;
  position: relative;
}
.slide-overlay {
  position: absolute; inset: 0;
  background: linear-gradient(90deg, rgba(0, 28, 56, 0.85) 0%, rgba(0, 40, 80, 0.55) 60%, rgba(0, 40, 80, 0.35) 100%);
  display: flex; align-items: center;
}
.slide-content { color: #fff; max-width: 680px; }
.slide-eyebrow {
  display: inline-block;
  letter-spacing: 1px;
  font-size: 0.85rem;
  color: #66b2ff;
  margin-bottom: 1rem;
  font-weight: 600;
}
.slide-title { font-size: 2.6rem; font-weight: 800; margin-bottom: 1rem; line-height: 1.2; }
.slide-description { font-size: 1.1rem; margin-bottom: 1.75rem; opacity: 0.92; }

/* 愿景 */
.vision-section { background: linear-gradient(135deg, #002850 0%, #0a4d8c 100%); }
.vision-headline { font-size: 2rem; font-weight: 800; }
.vision-sub { opacity: 0.85; font-size: 1.05rem; }
.pillar-card {
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 14px;
  padding: 2rem 1.5rem;
  transition: transform 0.3s ease, background 0.3s ease;
}
.pillar-card:hover { transform: translateY(-6px); background: rgba(255, 255, 255, 0.1); }
.pillar-icon {
  width: 56px; height: 56px; border-radius: 14px;
  background: linear-gradient(135deg, #1e90ff, #0a4d8c);
  display: flex; align-items: center; justify-content: center;
  font-size: 1.5rem; margin-bottom: 1.1rem;
}
.pillar-title { font-weight: 700; margin-bottom: 0.75rem; }
.pillar-text { opacity: 0.85; line-height: 1.8; font-size: 0.95rem; }
.vision-stat { font-size: 1.1rem; opacity: 0.9; }
.vision-stat strong { color: #66b2ff; font-size: 1.4rem; }

/* 公司介绍：完整品牌与业务语义，同时保持首页阅读层次 */
.company-profile-section {
  position: relative;
  overflow: hidden;
  background:
    linear-gradient(rgba(12, 48, 85, 0.035) 1px, transparent 1px),
    linear-gradient(90deg, rgba(12, 48, 85, 0.035) 1px, transparent 1px),
    linear-gradient(180deg, #f7fbff 0%, #eef6fc 100%);
  background-size: 34px 34px, 34px 34px, auto;
}
.profile-glow {
  position: absolute;
  border-radius: 50%;
  filter: blur(10px);
  pointer-events: none;
}
.profile-glow-one {
  width: 420px; height: 420px;
  top: -230px; right: -120px;
  background: radial-gradient(circle, rgba(47, 128, 237, 0.18), transparent 68%);
}
.profile-glow-two {
  width: 360px; height: 360px;
  bottom: -220px; left: -100px;
  background: radial-gradient(circle, rgba(32, 201, 151, 0.16), transparent 68%);
}
.company-profile-panel {
  padding: 3rem 3.2rem;
  border: 1px solid rgba(142, 177, 211, 0.35);
  border-radius: 22px;
  background: rgba(255, 255, 255, 0.9);
  box-shadow: 0 18px 55px rgba(15, 58, 98, 0.1);
}
.profile-heading {
  max-width: 860px;
  margin: 0 auto 2rem;
}
.profile-kicker {
  display: inline-block;
  margin-bottom: 0.75rem;
  color: #2f80ed;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.14em;
}
.profile-heading .section-title {
  color: #173a5e;
  margin-bottom: 0.65rem;
}
.profile-copy {
  column-count: 2;
  column-gap: 2.6rem;
}
.profile-copy p {
  margin-bottom: 1.15rem;
  color: #455d73;
  font-size: 0.96rem;
  line-height: 2;
  text-align: justify;
  break-inside: avoid;
}
.profile-copy p:first-child::first-letter {
  float: left;
  margin: 0.12rem 0.42rem 0 0;
  color: #2f80ed;
  font-size: 2.7rem;
  font-weight: 800;
  line-height: 0.9;
}
.profile-tags {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.65rem;
  margin-top: 1rem;
  padding-top: 1.35rem;
  border-top: 1px solid #e6eef6;
}
.profile-tags span {
  padding: 0.42rem 0.9rem;
  border: 1px solid rgba(47, 128, 237, 0.2);
  border-radius: 999px;
  background: rgba(47, 128, 237, 0.07);
  color: #245d94;
  font-size: 0.82rem;
  font-weight: 600;
}

/* 产品矩阵 */
.matrix-section { background: #f7f9fc; }
.section-title { font-weight: 700; }
.matrix-card {
  display: block; text-align: center; height: 100%;
  background: #fff; border-radius: 14px;
  padding: 1.75rem 1rem;
  box-shadow: 0 4px 16px rgba(0, 40, 80, 0.06);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  text-decoration: none; color: inherit;
}
.matrix-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 14px 34px rgba(0, 40, 80, 0.14);
  text-decoration: none; color: inherit;
}
.matrix-logo {
  width: 78px;
  height: 78px;
  margin: 0 auto 1rem;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 10px 24px rgba(0, 40, 80, 0.14);
}
.matrix-logo img {
  width: 100%;
  height: 100%;
  display: block;
}
.matrix-name { font-weight: 700; margin-bottom: 0.35rem; color: #1a1a1a; }
.matrix-desc { font-size: 0.85rem; color: #6c757d; margin-bottom: 0; }

/* 行业 */
.industry-item { text-align: center; transition: transform 0.3s ease; }
.industry-item:hover { transform: translateY(-5px); }
.industry-img {
  height: 120px; border-radius: 12px; overflow: hidden;
  margin-bottom: 0.75rem; box-shadow: 0 4px 14px rgba(0, 0, 0, 0.1);
}
.industry-img img { width: 100%; height: 100%; object-fit: cover; transition: transform 0.4s ease; }
.industry-item:hover .industry-img img { transform: scale(1.08); }

/* 研发实力 */
.strength-section {
  position: relative;
  background: linear-gradient(135deg, #06203b 0%, #0a4d8c 100%);
}
.stat-box {
  padding: 1.4rem 0.5rem;
  border-radius: 14px;
  transition: background 0.3s ease, transform 0.3s ease;
}
.stat-box:hover { background: rgba(255, 255, 255, 0.07); transform: translateY(-4px); }
.stat-num { font-size: 2.6rem; font-weight: 800; line-height: 1; color: #fff; }
.stat-suffix { font-size: 1.5rem; font-weight: 800; color: #66b2ff; margin-left: 2px; }
.stat-label { margin-top: 0.5rem; font-size: 0.95rem; opacity: 0.85; }

/* 轮播缓慢放大，增强质感 */
.swiper-slide-active .slide-bg { animation: heroZoom 8s ease-out forwards; }
@keyframes heroZoom {
  from { transform: scale(1.08); }
  to { transform: scale(1); }
}

@media (max-width: 768px) {
  .hero-slider, .slide-bg { height: 360px; }
  .slide-title { font-size: 1.7rem; }
  .slide-description { font-size: 0.95rem; }
  .slide-buttons .btn { display: block; width: 100%; margin-bottom: 0.5rem; }
  .vision-headline { font-size: 1.5rem; }
  .company-profile-panel { padding: 2rem 1.25rem; }
  .profile-copy { column-count: 1; }
  .profile-copy p { text-align: left; line-height: 1.9; }
}
</style>
